import React, { useState, useEffect } from 'react'
import QRCode from 'qrcode'
import { useSelector } from 'react-redux'
import { organizationApi } from '../../api/organizationApi'
import {
  Box,
  Typography,
  Grid,
  Paper,
  Card,
  CardContent,
  IconButton,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  LinearProgress,
  Avatar,
} from '@mui/material'
import {
  Refresh as RefreshIcon,
  People as PeopleIcon,
  Assignment as AssignmentIcon,
  Schedule as ScheduleIcon,
  Add as AddIcon,
  TrendingUp as TrendingUpIcon,
  ArrowForward as ArrowForwardIcon,
  Face as FaceIcon,
  AccessTime as AccessTimeIcon,
  Settings as SettingsIcon,
  QrCode2 as QrCode2Icon,
} from '@mui/icons-material'
import { useNavigate } from 'react-router-dom'
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, BarChart, Bar } from 'recharts'
import { useLoading } from '../../hooks'
import { StatusChip } from '../../components/common/Chips'
import LeaveConfigurationDialog from '../../components/OrgAdmin/LeaveConfigurationDialog'
import { dashboardApi } from '../../api/dashboardApi'
import { attendanceApi } from '../../api/attendanceApi'
import { shiftApi } from '../../api/shiftApi'
import TableSkeleton from '../../components/common/Skeleton/TableSkeleton'
import toast from 'react-hot-toast'




const OrgDashboard = () => {
  const navigate = useNavigate()
  const { loading, startLoading, stopLoading } = useLoading()
  const { orgId } = useSelector((state) => state.auth)
  const [orgName, setOrgName] = useState('Your Organization')
  const [openLeaveConfigDialog, setOpenLeaveConfigDialog] = useState(false)
  const [shiftsExist, setShiftsExist] = useState(true) // optimistic default




  const [departmentData, setDepartmentData] = useState([])
  const [attendanceTrend, setAttendanceTrend] = useState([])
  const [recentAttendance, setRecentAttendance] = useState([])
  const [stats, setStats] = useState({
    totalEmployees: 0,
    presentToday: 0,
    absentToday: 0,
    lateToday: 0,
    faceEnrolled: 0,
    shiftActive: 0,
    attendanceRate: 0,
  })

  const fetchStats = async () => {
    try {
      const response = await dashboardApi.getDashboardStats()
      if (response.data) {
        const data = response.data
        setStats({
          totalEmployees: data.total_employees,
          presentToday: data.attendance.present,
          absentToday: data.attendance.absent,
          lateToday: data.attendance.late,
          faceEnrolled: data.face_enrolled,
          shiftActive: data.active_shifts,
          attendanceRate: data.attendance_rate,
        })
      }
    } catch (error) {
      console.error('Error fetching dashboard stats:', error)
    }
  }

  const fetchDepartmentData = async () => {
    try {
      const response = await dashboardApi.getDepartmentWiseAttendance()
      if (response.data?.departments) {
        // Transform data to match chart keys
        const transformedData = response.data.departments.map(dept => ({
          department: dept.department,
          employees: dept.total_employees,
          present: dept.present,
          onLeave: dept.on_leave
        }))
        setDepartmentData(transformedData)
      }
    } catch (error) {
      console.error('Error fetching department data:', error)
    }
  }

  const fetchAttendanceTrend = async () => {
    try {
      const response = await dashboardApi.getHourlyCheckins()
      if (response.data?.hourly_checkins) {
        const hourlyData = response.data.hourly_checkins
        // Create range from 9 AM to 6 PM
        const hours = ['9 AM', '10 AM', '11 AM', '12 PM', '1 PM', '2 PM', '3 PM', '4 PM', '5 PM', '6 PM']
        const formattedData = hours.map(time => ({
          time,
          present: hourlyData[time] || 0
        }))
        setAttendanceTrend(formattedData)
      }
    } catch (error) {
      console.error('Error fetching hourly trends:', error)
    }
  }

  const fetchRecentAttendance = async () => {
    try {
      const response = await attendanceApi.getOrgAttendance({ page: 1, limit: 10 })
      if (response.data?.data) {
        setRecentAttendance(response.data.data)
      }
    } catch (error) {
      console.error('Error fetching recent attendance:', error)
    }
  }

  const fetchShiftsCheck = async () => {
    try {
      const response = await shiftApi.listShifts()
      const shiftList = response.data.shifts || []
      setShiftsExist(shiftList.length > 0)
    } catch (error) {
      console.error('Error checking shifts:', error)
    }
  }

  useEffect(() => {
    fetchStats()
    fetchDepartmentData()
    fetchAttendanceTrend()
    fetchRecentAttendance()
    fetchShiftsCheck()
    // Fetch org name for QR branding
    if (orgId) {
      organizationApi.getOrganizationDetails(orgId)
        .then(res => {
          const name = res.data?.name || res.data?.organization_name || res.data?.org_name || ''
          if (name) setOrgName(name)
        })
        .catch(() => { }) // silently fail — fallback name already set
    }
  }, [])

  const handleRefresh = () => {
    startLoading()
    fetchStats()
    fetchDepartmentData()
    fetchAttendanceTrend()
    fetchRecentAttendance()
    fetchShiftsCheck()
    setTimeout(() => stopLoading(), 1000)
  }

  const handleAddEmployee = () => {
    if (!shiftsExist) {
      toast.error('No shifts found. Please create a shift first before adding an employee.')
      navigate('/org/shifts')
      return
    }
    navigate('/org/employees/add')
  }

  const handleDownloadQR = async () => {
    if (!orgId) {
      toast.error('Organization ID not found. Please re-login.')
      return
    }
    try {
      const attendanceUrl = `${window.location.origin}/employee/test-face-io?org-id=${orgId}`

      // Generate QR with deep indigo color
      const qrDataUrl = await QRCode.toDataURL(attendanceUrl, {
        width: 420,
        margin: 1,
        color: { dark: '#1a237e', light: '#ffffff' },
      })

      // ── Canvas dimensions ─────────────────────────────────────────────────
      const W = 520
      const HEADER_H = 120
      const QR_PANEL_H = 480
      const FOOTER_H = 160
      const H = HEADER_H + QR_PANEL_H + FOOTER_H

      const canvas = document.createElement('canvas')
      canvas.width = W
      canvas.height = H
      const ctx = canvas.getContext('2d')

      // ── HEADER: solid deep indigo ─────────────────────────────────────────
      ctx.fillStyle = '#1a237e'
      ctx.beginPath()
      ctx.roundRect(0, 0, W, HEADER_H, [16, 16, 0, 0])
      ctx.fill()

      // Header: building icon circle
      ctx.fillStyle = 'rgba(255,255,255,0.18)'
      ctx.beginPath()
      ctx.arc(54, HEADER_H / 2, 30, 0, Math.PI * 2)
      ctx.fill()

      // Header: building emoji
      ctx.font = 'bold 28px Arial'
      ctx.textAlign = 'center'
      ctx.textBaseline = 'middle'
      ctx.fillStyle = '#ffffff'
      ctx.fillText('🏢', 54, HEADER_H / 2)

      // Header: org name
      const displayName = orgName.length > 28 ? orgName.slice(0, 26) + '…' : orgName
      ctx.font = 'bold 22px Arial'
      ctx.textAlign = 'left'
      ctx.fillStyle = '#ffffff'
      ctx.fillText(displayName, 98, HEADER_H / 2 - 10)

      // Header: sub-label
      ctx.font = '13px Arial'
      ctx.fillStyle = 'rgba(255,255,255,0.75)'
      ctx.fillText('Attendance Check-In / Check-Out', 98, HEADER_H / 2 + 14)

      // ── QR PANEL: light blue-grey background ──────────────────────────────
      ctx.fillStyle = '#f7f9fc'
      ctx.fillRect(0, HEADER_H, W, QR_PANEL_H)

      // White QR card with shadow
      const qrCardX = (W - 440) / 2
      const qrCardY = HEADER_H + 24
      ctx.shadowColor = 'rgba(0,0,0,0.12)'
      ctx.shadowBlur = 18
      ctx.fillStyle = '#ffffff'
      ctx.beginPath()
      ctx.roundRect(qrCardX, qrCardY, 440, 420, 12)
      ctx.fill()
      ctx.shadowBlur = 0

      // Draw QR code inside the white card
      const qrImg = new Image()
      await new Promise((resolve, reject) => {
        qrImg.onload = resolve
        qrImg.onerror = reject
        qrImg.src = qrDataUrl
      })
      ctx.drawImage(qrImg, qrCardX + 10, qrCardY + 10, 420, 400)

      // "Scan to Mark Attendance" label
      ctx.font = 'bold 15px Arial'
      ctx.textAlign = 'center'
      ctx.fillStyle = '#1a237e'
      ctx.fillText('📱  Scan to Mark Attendance', W / 2, HEADER_H + QR_PANEL_H - 18)

      // ── FOOTER: solid dark navy ───────────────────────────────────────────
      ctx.fillStyle = '#0d1b5e'
      ctx.beginPath()
      ctx.roundRect(0, HEADER_H + QR_PANEL_H, W, FOOTER_H, [0, 0, 16, 16])
      ctx.fill()

      const footerTop = HEADER_H + QR_PANEL_H

      // Footer title
      ctx.font = 'bold 14px Arial'
      ctx.textAlign = 'center'
      ctx.fillStyle = 'rgba(255,255,255,0.9)'
      ctx.fillText('HOW TO USE', W / 2, footerTop + 22)

      // Divider line
      ctx.strokeStyle = 'rgba(255,255,255,0.2)'
      ctx.lineWidth = 1
      ctx.beginPath()
      ctx.moveTo(40, footerTop + 36)
      ctx.lineTo(W - 40, footerTop + 36)
      ctx.stroke()

      // ── Check-In block (left half) ────────────────────────────────────────
      ctx.fillStyle = '#00e676'
      ctx.beginPath()
      ctx.roundRect(40, footerTop + 48, 10, 60, 5)
      ctx.fill()

      ctx.font = 'bold 13px Arial'
      ctx.textAlign = 'left'
      ctx.fillStyle = '#00e676'
      ctx.fillText('✅  Check-In', 60, footerTop + 65)

      ctx.font = '11px Arial'
      ctx.fillStyle = 'rgba(255,255,255,0.75)'
      ctx.fillText('First scan of the day', 60, footerTop + 83)
      ctx.fillText('marks you as PRESENT', 60, footerTop + 97)

      // ── Check-Out block (right half) ──────────────────────────────────────
      ctx.fillStyle = '#ff7043'
      ctx.beginPath()
      ctx.roundRect(W / 2 + 10, footerTop + 48, 10, 60, 5)
      ctx.fill()

      ctx.font = 'bold 13px Arial'
      ctx.fillStyle = '#ff7043'
      ctx.fillText('🚪  Check-Out', W / 2 + 30, footerTop + 65)

      ctx.font = '11px Arial'
      ctx.fillStyle = 'rgba(255,255,255,0.75)'
      ctx.fillText('Subsequent scans record', W / 2 + 30, footerTop + 83)
      ctx.fillText('your departure time', W / 2 + 30, footerTop + 97)

      // Footer bottom line
      ctx.font = '10px Arial'
      ctx.textAlign = 'center'
      ctx.fillStyle = 'rgba(255,255,255,0.45)'
      ctx.fillText('Face Recognition Attendance System  •  Powered by Intellisia', W / 2, footerTop + FOOTER_H - 12)

      // ── Export ────────────────────────────────────────────────────────────
      const finalDataUrl = canvas.toDataURL('image/png')
      const link = document.createElement('a')
      link.href = finalDataUrl
      link.download = `attendance-qr-${orgName.replace(/\s+/g, '-').toLowerCase() || orgId}.png`
      link.click()

      toast.success('QR card downloaded!')
    } catch (err) {
      console.error('QR generation failed:', err)
      toast.error('Failed to generate QR code. Please try again.')
    }
  }

  return (
    <Box sx={{ p: { xs: 1, md: 3 } }}>

      <Paper sx={{ p: { xs: 2, md: 3 }, borderRadius: 3, bgcolor: 'background.paper', boxShadow: 3 }}>
        {/* Header */}
        <Box sx={{ mb: 4 }}>
          <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, justifyContent: 'space-between', alignItems: { xs: 'flex-start', sm: 'center' }, gap: 2, mb: 2 }}>
            <Typography variant="h4" fontWeight="bold">
              Organization Dashboard
            </Typography>
            <Box sx={{ display: 'flex', gap: 1, width: { xs: '100%', sm: 'auto' } }}>
              <Button
                variant="outlined"
                onClick={handleRefresh}
                disabled={loading}
                startIcon={<RefreshIcon />}
                size="small"
                sx={{ flex: { xs: 1, sm: 'none' } }}
              >
                Refresh
              </Button>
              <Button
                variant="contained"
                startIcon={<AddIcon />}
                onClick={handleAddEmployee}
                sx={{ flex: { xs: 1, sm: 'none' } }}
              >
                Add Employee
              </Button>
            </Box>
          </Box>
          {loading && <LinearProgress sx={{ mb: 2 }} />}
        </Box>

        {/* Stats Cards */}
        <Grid container spacing={3} sx={{ mb: 4 }}>
          <Grid size={{ xs: 12, sm: 6, md: 3 }}>
            <Card variant="outlined" sx={{ height: '100%', borderRadius: 2 }}>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                  <Avatar sx={{ bgcolor: '#1976d2', mr: 2 }}>
                    <PeopleIcon />
                  </Avatar>
                  <Box>
                    <Typography variant="h4" fontWeight="bold">
                      {stats.totalEmployees}
                    </Typography>
                    <Typography variant="body2" color="textSecondary">
                      Total Employees
                    </Typography>
                  </Box>
                </Box>
                <Box sx={{ display: 'flex', gap: 1, mt: 1 }}>
                  <Chip
                    label={`${stats.faceEnrolled} Enrolled`}
                    size="small"
                    color="primary"
                    variant="outlined"
                  />
                </Box>
              </CardContent>
            </Card>
          </Grid>

          <Grid size={{ xs: 12, sm: 6, md: 3 }}>
            <Card variant="outlined" sx={{ height: '100%', borderRadius: 2 }}>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                  <Avatar sx={{ bgcolor: '#2e7d32', mr: 2 }}>
                    <AssignmentIcon />
                  </Avatar>
                  <Box>
                    <Typography variant="h4" fontWeight="bold">
                      {stats.presentToday}
                    </Typography>
                    <Typography variant="body2" color="textSecondary">
                      Present Today
                    </Typography>
                  </Box>
                </Box>
                <Box sx={{ display: 'flex', gap: 1, mt: 1 }}>
                  <Chip label={`${stats.absentToday} Absent`} size="small" color="error" variant="outlined" />
                  <Chip label={`${stats.lateToday} Late`} size="small" color="warning" variant="outlined" />
                </Box>
              </CardContent>
            </Card>
          </Grid>

          <Grid size={{ xs: 12, sm: 6, md: 3 }}>
            <Card variant="outlined" sx={{ height: '100%', borderRadius: 2 }}>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                  <Avatar sx={{ bgcolor: '#ed6c02', mr: 2 }}>
                    <ScheduleIcon />
                  </Avatar>
                  <Box>
                    <Typography variant="h4" fontWeight="bold">
                      {stats.shiftActive}
                    </Typography>
                    <Typography variant="body2" color="textSecondary">
                      Active Shifts
                    </Typography>
                  </Box>
                </Box>
                <Typography variant="caption" color="textSecondary">
                  Currently running
                </Typography>
              </CardContent>
            </Card>
          </Grid>

          <Grid size={{ xs: 12, sm: 6, md: 3 }}>
            <Card variant="outlined" sx={{ height: '100%', borderRadius: 2 }}>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                  <Avatar sx={{ bgcolor: '#9c27b0', mr: 2 }}>
                    <TrendingUpIcon />
                  </Avatar>
                  <Box>
                    <Typography variant="h4" fontWeight="bold">
                      {stats.attendanceRate}%
                    </Typography>
                    <Typography variant="body2" color="textSecondary">
                      Attendance Rate
                    </Typography>
                  </Box>
                </Box>
                <LinearProgress
                  variant="determinate"
                  value={stats.attendanceRate}
                  sx={{ mt: 1, height: 6, borderRadius: 3 }}
                  color="secondary"
                />
              </CardContent>
            </Card>
          </Grid>
        </Grid>

        {/* Main Content */}
        <Grid container spacing={3}>
          {/* Row 1: Charts */}
          <Grid size={{ xs: 12, md: 6 }} sx={{ minWidth: 0 }}>
            <Paper variant="outlined" sx={{ p: { xs: 1.5, md: 3 }, borderRadius: 2, height: '100%' }}>
              <Typography variant="h6" fontWeight="bold" gutterBottom>
                Today's Attendance Trend
              </Typography>
              <Box sx={{ height: 300, minHeight: 300, width: '100%', position: 'relative' }}>
                <ResponsiveContainer width="100%" height={300}>
                  <LineChart data={attendanceTrend}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="time" />
                    <YAxis />
                    <Tooltip />
                    <Line
                      type="monotone"
                      dataKey="present"
                      stroke="#1976d2"
                      strokeWidth={2}
                      dot={{ r: 4 }}
                      activeDot={{ r: 6 }}
                    />
                  </LineChart>
                </ResponsiveContainer>
              </Box>
            </Paper>
          </Grid>

          <Grid size={{ xs: 12, md: 6 }} sx={{ minWidth: 0 }}>
            <Paper variant="outlined" sx={{ p: { xs: 1.5, md: 3 }, borderRadius: 2, height: '100%' }}>
              <Typography variant="h6" fontWeight="bold" gutterBottom>
                Department-wise Attendance
              </Typography>
              <Box sx={{ height: 300, minHeight: 300, width: '100%', position: 'relative' }}>
                <ResponsiveContainer width="100%" height={300}>
                  <BarChart data={departmentData}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="department" />
                    <YAxis />
                    <Tooltip />
                    <Bar dataKey="employees" fill="#1976d2" name="Total" opacity={0.3} />
                    <Bar dataKey="present" fill="#2e7d32" name="Present" />
                    <Bar dataKey="onLeave" fill="#ed6c02" name="On Leave" />
                  </BarChart>
                </ResponsiveContainer>
              </Box>
            </Paper>
          </Grid>

          {/* Row 2: Tables & Actions */}
          <Grid size={{ xs: 12, md: 6 }}>
            <Paper variant="outlined" sx={{ p: { xs: 2, md: 3 }, borderRadius: 2, height: '100%' }}>
              <Typography variant="h6" fontWeight="bold" gutterBottom>
                Today's Attendance
              </Typography>
              <TableContainer sx={{ overflowX: 'auto' }}>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Employee</TableCell>
                      <TableCell>Department</TableCell>
                      <TableCell>Status</TableCell>
                      <TableCell>Check In/Out</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {loading ? (
                      <TableSkeleton columns={4} rows={5} />
                    ) : recentAttendance.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={4} align="center" sx={{ py: 4 }}>
                          <PeopleIcon sx={{ fontSize: 36, color: 'text.disabled', mb: 1 }} />
                          <Typography variant="body2" color="textSecondary">
                            No attendance records for today
                          </Typography>
                        </TableCell>
                      </TableRow>
                    ) : (
                      recentAttendance.map((emp) => (
                        <TableRow key={emp.id} hover>
                          <TableCell>
                            <Typography variant="body2">{emp.name}</Typography>
                            <Typography variant="caption" color="textSecondary">{emp.employee_code}</Typography>
                          </TableCell>
                          <TableCell>
                            <Chip label={emp.department} size="small" variant="outlined" />
                          </TableCell>
                          <TableCell>
                            <StatusChip status={emp.status} size="small" />
                          </TableCell>
                          <TableCell>
                            <Typography variant="caption" display="block">
                              In: {emp.check_in || '—'}
                            </Typography>
                            <Typography variant="caption" display="block">
                              Out: {emp.check_out || '—'}
                            </Typography>
                          </TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
              <Button
                fullWidth
                sx={{ mt: 2 }}
                endIcon={<ArrowForwardIcon />}
                onClick={() => navigate('/org/attendance')}
              >
                View Full Attendance
              </Button>
            </Paper>
          </Grid>

          <Grid size={{ xs: 12, md: 6 }}>
            <Paper variant="outlined" sx={{ p: { xs: 2, md: 3 }, borderRadius: 2, height: '100%' }}>
              <Typography variant="h6" fontWeight="bold" gutterBottom>
                Quick Actions
              </Typography>
              <Grid container spacing={2}>
                <Grid size={{ xs: 12, md: 6 }}>
                  <Card
                    variant="outlined"
                    sx={{
                      borderRadius: 2,
                      cursor: 'pointer',
                      bgcolor: 'background.paper',
                      transition: '0.3s',
                      height: '100%',
                      '&:hover': {
                        bgcolor: '#e3f2fd',
                        borderColor: 'primary.main',
                        transform: 'translateY(-2px)',
                        boxShadow: 2,
                        '& .MuiTypography-root': { color: '#000000' },
                        '& .MuiTypography-caption': { color: 'rgba(0, 0, 0, 0.6)' }
                      }
                    }}
                    onClick={() => navigate('/org/employees')}
                  >
                    <CardContent sx={{ display: 'flex', alignItems: 'center', gap: 2, p: 2 }}>
                      <Avatar sx={{ bgcolor: 'primary.main', width: 40, height: 40 }}>
                        <PeopleIcon fontSize="small" />
                      </Avatar>
                      <Box>
                        <Typography variant="subtitle1" fontWeight="bold" color="text.primary">
                          Manage Employees
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          View and manage employee records
                        </Typography>
                      </Box>
                    </CardContent>
                  </Card>
                </Grid>

                <Grid size={{ xs: 12, md: 6 }}>
                  <Card
                    variant="outlined"
                    sx={{
                      borderRadius: 2,
                      cursor: 'pointer',
                      bgcolor: 'background.paper',
                      transition: '0.3s',
                      height: '100%',
                      '&:hover': {
                        bgcolor: '#e1f5fe',
                        borderColor: 'info.main',
                        transform: 'translateY(-2px)',
                        boxShadow: 2,
                        '& .MuiTypography-root': { color: '#000000' },
                        '& .MuiTypography-caption': { color: 'rgba(0, 0, 0, 0.6)' }
                      }
                    }}
                    onClick={() => navigate('/org/employees?tab=enrollment')}
                  >
                    <CardContent sx={{ display: 'flex', alignItems: 'center', gap: 2, p: 2 }}>
                      <Avatar sx={{ bgcolor: 'info.main', width: 40, height: 40 }}>
                        <FaceIcon fontSize="small" />
                      </Avatar>
                      <Box>
                        <Typography variant="subtitle1" fontWeight="bold" color="text.primary">
                          Face Enrollment
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          Enroll employee biometrics
                        </Typography>
                      </Box>
                    </CardContent>
                  </Card>
                </Grid>

                <Grid size={{ xs: 12, md: 6 }}>
                  <Card
                    variant="outlined"
                    sx={{
                      borderRadius: 2,
                      cursor: 'pointer',
                      bgcolor: 'background.paper',
                      transition: '0.3s',
                      height: '100%',
                      '&:hover': {
                        bgcolor: '#e8f5e9',
                        borderColor: 'success.main',
                        transform: 'translateY(-2px)',
                        boxShadow: 2,
                        '& .MuiTypography-root': { color: '#000000' },
                        '& .MuiTypography-caption': { color: 'rgba(0, 0, 0, 0.6)' }
                      }
                    }}
                    onClick={() => navigate('/org/attendance')}
                  >
                    <CardContent sx={{ display: 'flex', alignItems: 'center', gap: 2, p: 2 }}>
                      <Avatar sx={{ bgcolor: 'success.main', width: 40, height: 40 }}>
                        <AssignmentIcon fontSize="small" />
                      </Avatar>
                      <Box>
                        <Typography variant="subtitle1" fontWeight="bold" color="text.primary">
                          Attendance Logs
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          Monitor daily attendance
                        </Typography>
                      </Box>
                    </CardContent>
                  </Card>
                </Grid>

                <Grid size={{ xs: 12, md: 6 }}>
                  <Card
                    variant="outlined"
                    sx={{
                      borderRadius: 2,
                      cursor: 'pointer',
                      bgcolor: 'background.paper',
                      transition: '0.3s',
                      height: '100%',
                      '&:hover': {
                        bgcolor: '#fff3e0',
                        borderColor: 'warning.main',
                        transform: 'translateY(-2px)',
                        boxShadow: 2,
                        '& .MuiTypography-root': { color: '#000000' },
                        '& .MuiTypography-caption': { color: 'rgba(0, 0, 0, 0.6)' }
                      }
                    }}
                    onClick={() => navigate('/org/shifts')}
                  >
                    <CardContent sx={{ display: 'flex', alignItems: 'center', gap: 2, p: 2 }}>
                      <Avatar sx={{ bgcolor: 'warning.main', width: 40, height: 40 }}>
                        <ScheduleIcon fontSize="small" />
                      </Avatar>
                      <Box>
                        <Typography variant="subtitle1" fontWeight="bold" color="text.primary">
                          Manage Shifts
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          Configure work shifts
                        </Typography>
                      </Box>
                    </CardContent>
                  </Card>
                </Grid>

                <Grid size={{ xs: 12, md: 6 }}>
                  <Card
                    variant="outlined"
                    sx={{
                      borderRadius: 2,
                      cursor: 'pointer',
                      bgcolor: 'background.paper',
                      transition: '0.3s',
                      height: '100%',
                      '&:hover': {
                        bgcolor: '#fce4ec',
                        borderColor: 'secondary.main',
                        transform: 'translateY(-2px)',
                        boxShadow: 2,
                        '& .MuiTypography-root': { color: '#000000' },
                        '& .MuiTypography-caption': { color: 'rgba(0, 0, 0, 0.6)' }
                      }
                    }}
                    onClick={() => navigate('/org/attendance/summary')}
                  >
                    <CardContent sx={{ display: 'flex', alignItems: 'center', gap: 2, p: 2 }}>
                      <Avatar sx={{ bgcolor: 'secondary.main', width: 40, height: 40 }}>
                        <AccessTimeIcon fontSize="small" />
                      </Avatar>
                      <Box>
                        <Typography variant="subtitle1" fontWeight="bold" color="text.primary">
                          Reports
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          View detailed reports
                        </Typography>
                      </Box>
                    </CardContent>
                  </Card>
                </Grid>

                <Grid size={{ xs: 12, md: 6 }}>
                  <Card
                    variant="outlined"
                    sx={{
                      borderRadius: 2,
                      cursor: 'pointer',
                      bgcolor: 'background.paper',
                      transition: '0.3s',
                      height: '100%',
                      '&:hover': {
                        bgcolor: '#e1bee7', // Light purple
                        borderColor: '#9c27b0', // Purple
                        transform: 'translateY(-2px)',
                        boxShadow: 2,
                        '& .MuiTypography-root': { color: '#000000' },
                        '& .MuiTypography-caption': { color: 'rgba(0, 0, 0, 0.6)' }
                      }
                    }}
                    onClick={() => navigate('/org/attendance/summary')}
                  >
                    <CardContent sx={{ display: 'flex', alignItems: 'center', gap: 2, p: 2 }}>
                      <Avatar sx={{ bgcolor: '#9c27b0', width: 40, height: 40 }}>
                        <TrendingUpIcon fontSize="small" />
                      </Avatar>
                      <Box>
                        <Typography variant="subtitle1" fontWeight="bold" color="text.primary">
                          Analytics
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          Analyze attendance trends
                        </Typography>
                      </Box>
                    </CardContent>
                  </Card>
                </Grid>

                <Grid size={{ xs: 12 }}>
                  <Card
                    variant="outlined"
                    sx={{
                      borderRadius: 2,
                      cursor: 'pointer',
                      bgcolor: 'background.paper',
                      transition: '0.3s',
                      height: '100%',
                      '&:hover': {
                        bgcolor: '#f5f5f5',
                        borderColor: 'grey.500',
                        transform: 'translateY(-2px)',
                        boxShadow: 2,
                        '& .MuiTypography-root': { color: '#000000' },
                        '& .MuiTypography-caption': { color: 'rgba(0, 0, 0, 0.6)' }
                      }
                    }}
                    onClick={() => setOpenLeaveConfigDialog(true)}
                  >
                    <CardContent sx={{ display: 'flex', alignItems: 'center', gap: 2, p: 2 }}>
                      <Avatar sx={{ bgcolor: 'grey.600', width: 40, height: 40 }}>
                        <SettingsIcon fontSize="small" />
                      </Avatar>
                      <Box>
                        <Typography variant="subtitle1" fontWeight="bold" color="text.primary">
                          Leave Configuration
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          Manage leave policies and settings
                        </Typography>
                      </Box>
                    </CardContent>
                  </Card>
                </Grid>

                {/* QR Code Download */}
                <Grid size={{ xs: 12 }}>
                  <Card
                    variant="outlined"
                    sx={{
                      borderRadius: 2,
                      cursor: 'pointer',
                      bgcolor: 'background.paper',
                      transition: '0.3s',
                      border: '1px dashed',
                      borderColor: '#0097a7',
                      '&:hover': {
                        bgcolor: '#e0f7fa',
                        borderColor: '#0097a7',
                        borderStyle: 'solid',
                        transform: 'translateY(-2px)',
                        boxShadow: 2,
                        '& .MuiTypography-root': { color: '#000000' },
                        '& .MuiTypography-caption': { color: 'rgba(0, 0, 0, 0.6)' }
                      }
                    }}
                    onClick={handleDownloadQR}
                  >
                    <CardContent sx={{ display: 'flex', alignItems: 'center', gap: 2, p: 2 }}>
                      <Avatar sx={{ bgcolor: '#0097a7', width: 40, height: 40 }}>
                        <QrCode2Icon fontSize="small" />
                      </Avatar>
                      <Box>
                        <Typography variant="subtitle1" fontWeight="bold" color="text.primary">
                          Download Attendance QR
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          Print &amp; display at office entrance for quick check-in
                        </Typography>
                      </Box>
                    </CardContent>
                  </Card>
                </Grid>
              </Grid>
            </Paper>
          </Grid>
        </Grid>
      </Paper>

      <LeaveConfigurationDialog
        open={openLeaveConfigDialog}
        onClose={() => setOpenLeaveConfigDialog(false)}
      />
    </Box>
  )
}

export default OrgDashboard
